// src/context/AuthContext.js
import React, { createContext, useState, useEffect, useContext } from 'react';
import { auth, db } from '../firebase';
import { onAuthStateChanged, signOut, signInWithEmailAndPassword } from 'firebase/auth';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';

export const AuthContext = createContext();
export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState('');
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Enhanced login function to ensure proper role fetching
  const login = async (email, password) => {
    try {
      // First, fully clear previous state
      await signOut(auth);
      setCurrentUser(null);
      setUserRole('');
      setUserData(null);
      
      // Now perform new login
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      
      // Immediately fetch user data to ensure role is correct
      const userDoc = await getDoc(doc(db, "users", userCredential.user.uid));
      if (userDoc.exists()) {
        const data = userDoc.data();
        console.log("Login - User role:", data.role); // Debug log
        
        // Set user data explicitly
        setCurrentUser(userCredential.user);
        setUserRole(data.role);
        setUserData(data);
        
        // Update last login timestamp
        await updateDoc(doc(db, "users", userCredential.user.uid), {
          lastLogin: serverTimestamp()
        });
        
        return {
          user: userCredential.user,
          userData: data
        };
      } else {
        throw new Error("User data not found in database");
      }
    } catch (error) {
      console.error("Login error:", error);
      throw error;
    }
  };

  // Improved logout function that properly clears all state
  const logout = async () => {
    console.log("Logging out - clearing state"); // Debug log
    
    try {
      // Clear all auth state before sign out
      setCurrentUser(null);
      setUserRole('');
      setUserData(null);
      
      // Then sign out from Firebase
      await signOut(auth);
      
      // Force reload to clear any cached state
      // This is important when switching between users with different roles
      window.location.href = '/login';
      
      return true;
    } catch (error) {
      console.error("Logout error:", error);
      throw error;
    }
  };

  // Add function to check if user has required role
  const hasRole = (requiredRole) => {
    console.log("Checking role:", requiredRole, "User has role:", userRole); // Debug log
    
    if (!userRole) return false;
    
    if (requiredRole === 'admin') {
      return userRole === 'admin';
    } else if (requiredRole === 'mentor') {
      return userRole === 'admin' || userRole === 'mentor';
    } else if (requiredRole === 'learner') {
      return userRole === 'admin' || userRole === 'mentor' || userRole === 'learner';
    }
    
    return false;
  };

  // Add this function to determine redirect paths based on user role
  const getRedirectPath = (role) => {
    console.log("Getting redirect path for role:", role); // Debug log
    
    switch (role) {
      case "admin":
        return "/admin";
      case "mentor":
        return "/mentor";
      case "learner":
        return "/learner";
      default:
        return "/login";
    }
  };

  useEffect(() => {
    console.log("Setting up auth state listener"); // Debug log
    
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log("Auth state changed:", user ? "User logged in" : "No user"); // Debug log
      
      if (user) {
        try {
          // Get user role from Firestore
          const userDocRef = doc(db, "users", user.uid);
          const userDoc = await getDoc(userDocRef);
          
          if (userDoc.exists()) {
            const data = userDoc.data();
            console.log("Auth state - User role:", data.role); // Debug log
            
            setCurrentUser(user);
            setUserRole(data.role);
            setUserData(data);
          } else {
            console.error("User doc not found in Firestore");
            setCurrentUser(null);
            setUserRole('');
            setUserData(null);
            await signOut(auth);
          }
        } catch (error) {
          console.error("Error fetching user data:", error);
          setCurrentUser(null);
          setUserRole('');
          setUserData(null);
        }
      } else {
        // Clear all state when no user is detected
        setCurrentUser(null);
        setUserRole('');
        setUserData(null);
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userRole,
    userData,
    login,
    logout,
    loading,
    getRedirectPath,
    hasRole
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

// hook for easier access
export function useAuth() {
  return useContext(AuthContext);
}